import { skipToken } from "@reduxjs/toolkit/query";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import AHDate from "_shared/dates/date";
import type Guid from "_shared/guids/guid";
import type { PermissionKind } from "permissions/_api/permission-kinds-api-service";
import PermissionFilterParameters from "permissions/_api/queries/filtering/permission-filter-parameters";
import PermissionOrderParameters, { PermissionOrderField } from "permissions/_api/queries/ordering/permission-order-params";
import PermissionQuery, { PermissionView } from "permissions/_api/queries/permission-query";
import { useGetPermissionsByGranteeQuery, useGetPermissionsQuery } from "permissions/_api/permissions-api-service";
import { standingResourceTypes } from "permissions/_views/people/person-grants-model";
import Permission from "permissions/permission";
import PermissionsByGrantee from "permissions/by-grantee/permissions-by-grantee";
import { useMemo } from "react";

/**
 * One page is plenty for the live standing grants: there are some thirty standing kinds, and the
 * server keeps one live row per resource. Removed rows accumulate, so only the latest are read.
 */
const PAGE = PaginationParameters.create({ pageIndex: 0, pageSize: 100 });

/** Before any grant: the floor that makes `minDisabledDate` mean "removed at any time". */
const BEFORE_ANY_REMOVAL = new AHDate("2000-01-01");

/**
 * The person page's reads, each fetched exactly:
 *
 * - **Live standing grants**: `GET /permissions?view=Flat&granteeUserIds=X&resourceTypes=<standing
 *   types>`. The flat view carries parents one level deep, which the Source column draws; the grantee
 *   view's previews carry none.
 * - **Removed standing grants**: the same read for rows with a removal date, latest first.
 * - **Counts**: `view=Grantee&includePermissionPreviews=false`, one row per resource type, for the
 *   per-record counts in the summary strip; and the same for removed grants, for the Removed filter.
 */
function queriesFor(userId: Guid, resourceTypes: string[]) {
  const live = new PermissionQuery({
    permissionView: PermissionView.Flat,
    paginationParameters: PAGE,
    filterParameters: new PermissionFilterParameters({ granteeUserIds: [userId], resourceTypes, showDisabled: false }),
  });
  const removed = new PermissionQuery({
    permissionView: PermissionView.Flat,
    paginationParameters: PAGE,
    orderParameters: new PermissionOrderParameters({
      orderBy: PermissionOrderField.DisabledDate,
      orderDir: OrderDirection.Descending,
    }),
    filterParameters: new PermissionFilterParameters({
      granteeUserIds: [userId],
      resourceTypes,
      minDisabledDate: BEFORE_ANY_REMOVAL,
    }),
  });
  const counts = new PermissionQuery({
    permissionView: PermissionView.Grantee,
    includePermissionPreviews: false,
    paginationParameters: PaginationParameters.create({ pageIndex: 0, pageSize: 1 }),
    filterParameters: new PermissionFilterParameters({ granteeUserIds: [userId] }),
  });
  // The same count of removed grants, for the per-record kinds the Removed filter lists. A kind whose
  // every grant is removed has no live count, and would otherwise have no row to reach them from.
  const removedCounts = new PermissionQuery({
    permissionView: PermissionView.Grantee,
    includePermissionPreviews: false,
    paginationParameters: PaginationParameters.create({ pageIndex: 0, pageSize: 1 }),
    filterParameters: new PermissionFilterParameters({ granteeUserIds: [userId], minDisabledDate: BEFORE_ANY_REMOVAL }),
  });
  return { live, removed, counts, removedCounts };
}

export type PersonGrants = Readonly<{
  grants: readonly Permission[];
  permissionCounts?: Readonly<Record<string, number>>;
  /** The person's removed grants by resource type. */
  removedPermissionCounts?: Readonly<Record<string, number>>;
  isLoading: boolean;
  /** The grants could not be read: the page's one page-level error. A failed count is not one. */
  error?: string;
  retry: () => void;
}>;

export default function usePersonGrants(userId: Guid | undefined, kinds: readonly PermissionKind[] | undefined): PersonGrants {
  // Memoized on purpose: the queries are class instances that reach RTK Query's arguments, and a
  // fresh one each render would be a fresh cache key only while the compiler happens to bail.
  const queries = useMemo(
    () => (userId && kinds ? queriesFor(userId, standingResourceTypes(kinds)) : undefined),
    [userId, kinds],
  );

  // Refetched on focus, so a page left open does not keep showing grants another administrator
  // has since removed. The permissions realtime event that would push this is a later issue.
  const live = useGetPermissionsQuery(queries ? { query: queries.live } : skipToken, { refetchOnFocus: true });
  const removed = useGetPermissionsQuery(queries ? { query: queries.removed } : skipToken, { refetchOnFocus: true });
  const counts = useGetPermissionsByGranteeQuery(queries ? { query: queries.counts } : skipToken, { refetchOnFocus: true });
  const removedCounts = useGetPermissionsByGranteeQuery(queries ? { query: queries.removedCounts } : skipToken, {
    refetchOnFocus: true,
  });

  const grants = useMemo(() => {
    const all = [...(live.data?.data ?? []), ...(removed.data?.data ?? [])];
    const seen = new Set<string>();
    return Permission.fromJSONArray(all).filter((grant) => {
      const id = grant.id?.value;
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }, [live.data, removed.data]);

  const permissionCounts = useMemo(
    () => (counts.data?.data[0] ? PermissionsByGrantee.fromJSON(counts.data.data[0]).permissionCounts : undefined),
    [counts.data],
  );

  const removedPermissionCounts = useMemo(
    () =>
      removedCounts.data?.data[0] ? PermissionsByGrantee.fromJSON(removedCounts.data.data[0]).permissionCounts : undefined,
    [removedCounts.data],
  );

  const failure = live.error ?? removed.error;
  return {
    grants,
    permissionCounts,
    removedPermissionCounts,
    isLoading: live.isLoading || removed.isLoading,
    error: failure ? (getErrorMessageFromUnknown(failure) ?? "The grants could not be loaded.") : undefined,
    retry: () => {
      void live.refetch();
      void removed.refetch();
    },
  };
}
